import { useMutation, useQuery } from '@apollo/client'
import {
  Dialog,
  DialogVariant,
  Text,
  TextHighlight,
  TextSize
} from '@focaldata/cin-ui-components'
import React, { useState } from 'react'
import { LoggerErrorType } from '../../../data/logger'
import { useOptionalWaveId } from '../../../hooks/useOptionalWaveId'
import { useProjectId } from '../../../hooks/useProjectId'
import { captureApolloError } from '../../../utils/HelperFunctions'
import { IMPORT_QUESTIONNAIRE_ENTRIES } from './ImportQuestions.mutations'
import { IMPORT_SOURCE_WAVE } from './ImportQuestions.query'
import ImportQuestionsPicker from './ImportQuestionsPicker/ImportQuestionsPicker.container'
import ImportQuestionsSurveyPicker from './ImportQuestionsSurveyPicker/ImportQuestionsSurveyPicker.container'

interface Props {
  position: number
  refetchQuestionnaire: () => void
  onClose: () => void
}

const ImportQuestions: React.FC<Props> = ({
  onClose,
  refetchQuestionnaire,
  position
}: Props) => {
  const projectId = useProjectId()
  const waveId = useOptionalWaveId()

  const [selectedSurveyIds, setSelectedSurveyIds] = useState<
    [string, string] | undefined
  >()
  const [questionSelection, setQuestionSelection] = useState<string[]>([])
  const [importing, setImporting] = useState<boolean>(false)

  const [importQuestionnaireEntries] = useMutation(
    IMPORT_QUESTIONNAIRE_ENTRIES,
    {
      context: { clientName: 'questionnaire' },
      onError: (error) => {
        captureApolloError(
          LoggerErrorType.ApolloMutation,
          'importQuestionnaireEntriesMutation',
          error
        )
      }
    }
  )

  const [selectedProjectId, selectedSurveyId] = selectedSurveyIds || ['', '']

  const {
    data: sourceWaveData,
    loading: sourceWaveLoading,
    error: sourceWaveError
  } = useQuery(IMPORT_SOURCE_WAVE, {
    context: { clientName: 'dashboard' },
    variables: { surveyId: selectedSurveyId },
    skip: !selectedSurveyIds,
    onError: (error) => {
      captureApolloError(LoggerErrorType.ApolloQuery, 'importSourceWave', error)
    }
  })
  const sourceWaveId = sourceWaveData?.waveBySurveyId?.waveId

  const isImportButtonDisabled =
    selectedSurveyIds === undefined ||
    questionSelection.length === 0 ||
    sourceWaveLoading ||
    sourceWaveError !== undefined

  const handleImportQuestions = async (): Promise<void> => {
    if (selectedSurveyIds && !isImportButtonDisabled) {
      setImporting(true)
      await importQuestionnaireEntries({
        variables: {
          input: {
            projectId,
            waveId,
            sourceProjectId: selectedProjectId,
            sourceWaveId,
            entryIds: questionSelection,
            position
          }
        }
      })
      setImporting(false)
      // this doesn't seem to be quite right
      // TODO: importQuestionnaireEntriesV2 should return the list of target entries to get rid of refetching; currently, it returns the list of source entries
      refetchQuestionnaire()
      onClose()
    }
  }

  const handleBackButton = (): void => {
    setSelectedSurveyIds(undefined)
    setQuestionSelection([])
  }

  return (
    <Dialog
      onClose={onClose}
      open
      statusOnRight
      fullWidth
      variant={DialogVariant.SingleButtonStatus}
      statusComponent={
        <Text size={TextSize.ms} highlight={TextHighlight.Background}>
          {isImportButtonDisabled
            ? undefined
            : `${questionSelection.length} selected`}
        </Text>
      }
      primaryButtonLoading={importing}
      primaryButtonDisabled={isImportButtonDisabled}
      title="Import from existing survey"
      primaryButtonText="Import"
      primaryButtonClick={handleImportQuestions}
    >
      {!selectedSurveyIds && (
        <ImportQuestionsSurveyPicker
          onSurveypicked={(projectId, surveyId) => {
            setSelectedSurveyIds([projectId, surveyId])
          }}
        />
      )}
      {selectedSurveyIds && (
        <ImportQuestionsPicker
          projectId={selectedProjectId}
          surveyId={selectedSurveyId}
          waveId={sourceWaveId}
          waveLoading={sourceWaveLoading}
          waveError={sourceWaveError !== undefined}
          onBackButton={handleBackButton}
          questionSelection={questionSelection}
          setQuestionSelection={setQuestionSelection}
        />
      )}
    </Dialog>
  )
}

export default React.memo(ImportQuestions)
